<template>
  <v-expansion-panels v-model="apartmentPanel">
    <v-expansion-panel>
      <v-expansion-panel-header class="text-h6 font-weight-regular">
        <v-row>
          <v-col md="auto">
            {{ $t('APARTMENTS.ITEM') }}
            <v-icon
              v-if="!editMode && isInhabitant"
              class="ml-2"
              color="indigo"
              @click.stop="toggleEditMode"
            >
              mdi-square-edit-outline
            </v-icon>
          </v-col>
          <v-spacer> </v-spacer>
          <v-col md="auto">
            <div v-if="isPersonWithoutRoles" class="text-subtitle-1 grey--text">
              {{ $t('APARTMENTS.PERSON_ACCESS') }}
            </div>
          </v-col>
          <v-spacer> </v-spacer>
          <v-col md="auto">
            <v-btn
              v-if="!isInhabitant && !editMode"
              class="mr-2"
              outlined
              :disabled="isPersonWithoutRoles"
              color="indigo"
              v-on="{
                click: [() => (apartmentPanel = 1), toggleEditMode]
              }"
            >
              {{ $t('APARTMENTS.CHECKIN') }}
            </v-btn>
            <v-btn
              v-else-if="isInhabitant"
              class="mr-2"
              dark
              outlined
              color="indigo"
              @click.stop="dialogCheckout = true"
            >
              {{ $t('APARTMENTS.CHECKOUT') }}
            </v-btn>

            <v-dialog
              v-if="dialogCheckout"
              v-model="dialogCheckout"
              persistent
              max-width="600px"
            >
              <check-out-card
                v-model="form"
                @update="updateCheckout"
                @cancel="closeDialog"
              />
            </v-dialog>
          </v-col>
        </v-row>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-form ref="inhabitantCheckinForm" v-model="valid">
          <v-row>
            <v-col>
              <inhabitant-form
                v-model="form.inhabitant"
                :readonly="!editMode"
                :errors="errors.get('inhabitant')"
                @update:apartment_id="changeApartmentInfo"
                @update-prop="clearErrorByName($event, 'inhabitant')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <apartment-information
                v-if="showApartmentInformation"
                :apartment="form.inhabitant.apartment"
                :cohabitants="cohabitants"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pt-0">
              <div v-if="editMode">
                <v-btn
                  outlined
                  class="mr-5"
                  depressed
                  color="grey"
                  large
                  @click="cancel"
                >
                  {{ $t('BUTTON.CANCEL') }}
                </v-btn>
                <v-btn
                  v-if="form.inhabitant && form.inhabitant.id"
                  depressed
                  type="submit"
                  :disabled="!valid"
                  large
                  color="primary"
                  class="mr-5"
                  @click.prevent="updateInhabitant"
                >
                  {{ $t('BUTTON.UPDATE') }}
                </v-btn>
                <v-btn
                  v-else
                  depressed
                  type="submit"
                  :disabled="!valid"
                  large
                  color="primary"
                  class="mr-5"
                  @click.prevent="createInhabitant"
                >
                  {{ $t('BUTTON.CREATE') }}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import InhabitantForm from '@/modules/persons/components/forms/InhabitantForm';
import CheckOutCard from '@/modules/persons/components/CheckOutCard';
import ApartmentInformation from '@/modules/persons/components/ApartmentInformation';
import errors from '@/modules/_global/mixins/errors';
import { onceRequest } from '@/modules/_global/utils/common';
import { cloneDeep, isEqual, isEmpty } from 'lodash';
import { mapActions } from 'vuex';

export default {
  name: 'Apartment',

  components: {
    InhabitantForm,
    CheckOutCard,
    ApartmentInformation
  },

  mixins: [errors],

  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },

  data: () => ({
    form: {},
    valid: false,
    editMode: false,
    apartmentPanel: 0,
    dialogCheckout: false,
    cohabitants: []
  }),

  computed: {
    isPersonWithoutRoles() {
      return !(
        this.form.user &&
        this.form.user.roles &&
        this.form.user.roles.length
      );
    },

    isInhabitant() {
      return !!(
        this.form.inhabitant && this.form.inhabitant.hasOwnProperty('id')
      );
    },

    showApartmentInformation() {
      return this.form.inhabitant && this.form.inhabitant.apartment;
    }
  },

  watch: {
    value: {
      deep: true,
      immediate: true,
      handler: 'valueWatchHandler'
    },
    'form.inhabitant': 'loadCohabitants'
  },

  methods: {
    ...mapActions('inhabitant', ['getCohabitants']),
    ...mapActions('apartment', ['getApartmentInfo']),
    ...mapActions('inhabitant', ['store', 'update']),

    valueWatchHandler(formProps) {
      if (isEqual(formProps, this.form)) {
        return;
      }

      this.form = {
        ...this.form,
        ...cloneDeep(formProps)
      };

      if (formProps.inhabitant === null) {
        this.form.inhabitant = {};
        this.formCheckInReset();
      }

      if (!this.apartmentPanel && !this.isInhabitant) {
        this.apartmentPanel = 1;
      }
    },

    emitter() {
      this.$emit('input', this.form);
    },

    toggleEditMode() {
      this.editMode = !this.editMode;
    },

    cancel() {
      this.toggleEditMode();
      this.form = cloneDeep(this.value);
      this.$refs.inhabitantCheckinForm.resetValidation();
    },

    closeDialog() {
      this.dialogCheckout = false;
    },

    formCheckInReset() {
      if (this.$refs.inhabitantCheckinForm) {
        this.$refs.inhabitantCheckinForm.reset();
      }
    },

    updateCheckout() {
      this.closeDialog();
      this.$emit('update');
    },

    updateInhabitant: onceRequest(function updateInhabitant() {
      return this.update(this.form.inhabitant)
        .then(response => {
          this.emitter();
          this.$emit('update', response.data);
          this.toggleEditMode();
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    }),

    createInhabitant: onceRequest(function createInhabitant() {
      return this.store({ ...this.form.inhabitant, person_id: this.form.id })
        .then(response => {
          this.emitter();
          this.$emit('update', response.data);
          this.toggleEditMode();
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    }),

    clearCohabitants() {
      this.cohabitants = [];
    },

    loadCohabitants() {
      if (isEmpty(this.form.inhabitant) && this.cohabitants.length) {
        this.clearCohabitants();
      }

      if (this.isInhabitant) {
        this.getCohabitants(this.form.inhabitant.id).then(({ data }) => {
          this.cohabitants = data.data;
        });
      }
    },

    changeApartmentInfo() {
      const apartmentId = this.form.inhabitant.apartment_id;

      if (apartmentId) {
        this.getApartmentInfo(apartmentId).then(({ data }) => {
          this.cohabitants = data.data.inhabitants.filter(
            item => item.person_id !== this.form.id
          );
          this.form.inhabitant.apartment = data.data;
        });
      } else {
        this.clearCohabitants();
      }
    }
  }
};
</script>

<style scoped></style>
